import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Container,
  Box,
  Chip,
  Button,
  TextField,
  Select,
  MenuItem,
  IconButton,
  TablePagination,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Menu from "@mui/material/Menu";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { PartnerArrowIcon } from "../../res/NewWebIcons";
import IpinModal from "../IpaisaMpin/IpinModal";
import SetPasswordModal from "../IpaisaMpin/SetIPMpin";

const ManuallyApprovedTxnReport = () => {
  const [txnList, setTxnList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editFormData, setEditFormData] = useState({ utr: "" });

  const [currPage, setCurrPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalElements, setTotalElements] = useState(0);

  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    searchBy: "",
  });
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);

  // IPIN Modal States
  const [isIpinModalOpen, setIpinModalOpen] = useState(false);
  const [openSetPasswordModal, setOpenSetPasswordModal] = useState(false);
  const [pendingActionPayload, setPendingActionPayload] = useState(null);

  // IPIN Handling Functions
  const triggerIpinVerification = (payload) => {
    console.log("[ManuallyApprovedTxnReport] triggerIpinVerification called. Payload:", payload);
    const isTxnMpinSet = sessionStorage.getItem("isTxnMpinSet");
    console.log("[ManuallyApprovedTxnReport] sessionStorage isTxnMpinSet:", isTxnMpinSet);

    setPendingActionPayload(payload);
    if (isTxnMpinSet === "False") {
      console.log("[ManuallyApprovedTxnReport] Setting openSetPasswordModal to true");
      setOpenSetPasswordModal(true);
    } else {
      console.log("[ManuallyApprovedTxnReport] Setting isIpinModalOpen to true");
      setIpinModalOpen(true);
    }
  };

  const handleIpinVerificationSuccess = async (ipin) => {
    console.log("[ManuallyApprovedTxnReport] IPIN verification successful with ipin:", ipin);
    setIpinModalOpen(false);
    setOpenSetPasswordModal(false);

    if (pendingActionPayload) {
      await updateTransactionAPI(
        pendingActionPayload.externalRefId,
        pendingActionPayload.utr,
        pendingActionPayload.Status
      );
      setPendingActionPayload(null);
    }
  };

  const handleCloseIpinModals = () => {
    setLoading(false);
    setIpinModalOpen(false);
    setOpenSetPasswordModal(false);
    setPendingActionPayload(null);
  };

  const fetchTransactions = async (
    page = 0,
    size = 25,
    currentFilters = filters
  ) => {
    setLoading(true);
    const token = sessionStorage.getItem("jwtToken");
    try {
      const params = {
        page: page,
        size: size,
        fromDate: currentFilters.startDate
          ? dayjs(currentFilters.startDate).format("YYYY-MM-DD")
          : "",
        toDate: currentFilters.endDate
          ? dayjs(currentFilters.endDate).format("YYYY-MM-DD")
          : "",
        searchBy: currentFilters.searchBy || "",
      };
      console.log("Fetching transactions with params:", params);
      // console.log("Full API Response Data:", JSON.stringify(response.data, null, 2)); // Keep this commented for now, or remove if not needed

      const response = await axios.get(
        "https://api.ipaisa.co.in/myapp/auth/gatAllManuallyApprovedTxns",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: params,
        }
      );
      // console.log("Full API Response Data:", JSON.stringify(response.data, null, 2)); // Log after getting response

      // Main transaction list is in response.data.data
      // Nested payout object is within each transaction item
      if (
        response.data?.message === "Data found" ||
        Array.isArray(response.data?.data)
      ) {
        setTxnList(response.data.data || []); // Corrected to use response.data.data
        setTotalElements(response.data.totalElements || 0);
      } else if (response.data?.message === "No data found") {
        setTxnList([]);
        setTotalElements(0);
        // toast.info("No data found for the selected criteria.");
      } else {
        // toast.error(response.data?.message || "Failed to fetch transactions");
        setTxnList([]);
        setTotalElements(0);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      toast.error("Error fetching transactions");
      setTxnList([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions(currPage, pageSize, filters);
  }, []);

  const handleEditFormChange = (field, value) => {
    setEditFormData({ ...editFormData, [field]: value });
  };

  const updateTransactionAPI = async (externalRefValue, utr, status) => {
    const token = sessionStorage.getItem("jwtToken");
    try {
      const response = await axios.post(
        `https://api.ipaisa.co.in/myapp/auth/handleModifiedtxns`,
        {
          Status: status,
          externalRefId: externalRefValue,
          utr: utr,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (
        response.status === 200 ||
        response.data?.message === "Data updated" ||
        response.data?.message === "Transaction updated successfully"
      ) {
        toast.success(`Transaction ${status.toLowerCase()} successfully`);
        if (status === "Reject" && filters.searchBy) {
          const newFilters = {
            ...filters,
            searchBy: "",
          };
          setFilters(newFilters);
          setCurrPage(0);
          fetchTransactions(0, pageSize, newFilters);
        } else {
          fetchTransactions(currPage, pageSize, filters);
        }
        setEditingRowId(null);
      } else {
        toast.error(
          response.data?.message ||
            `Failed to ${status.toLowerCase()} transaction`
        );
      }
    } catch (error) {
      console.error(`Error ${status.toLowerCase()} transaction:`, error);
      toast.error(
        `Error ${status.toLowerCase()} transaction: ` +
          (error.response?.data?.message || error.message)
      );
    }
  };

  const handleSaveModifiedUTR = (transactionId) => {
    updateTransactionAPI(transactionId, editFormData.utr, "Modified");
  };

  const handleRejectTransaction = (transactionId, currentUtr) => {
    const payload = {
      Status: "Reject",
      externalRefId: transactionId,
      utr: currentUtr,
    };
    triggerIpinVerification(payload);
  };

  const handleCancelEdit = () => {
    setEditingRowId(null);
  };

  const handleEditRow = (txn) => {
    setEditingRowId(txn.payout?.externalRef || txn.id);
    setEditFormData({ utr: txn.payout?.txnReferenceId || "" });
  };

  const handleChangePage = (event, newPage) => {
    setCurrPage(newPage);
    fetchTransactions(newPage, pageSize, filters);
  };

  const handleChangeRowsPerPage = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    setCurrPage(0);
    fetchTransactions(0, newPageSize, filters);
  };

  const handleFilterMenuClick = (event) => {
    setFilterMenuAnchor(event.currentTarget);
  };

  const handleFilterMenuClose = () => {
    setFilterMenuAnchor(null);
  };

  const handleDateChange = (key, newValue) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: newValue,
    }));
  };

  const handleSearchChange = (event) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      searchBy: event.target.value,
    }));
  };

  const applyFilters = () => {
    handleFilterMenuClose();
    if (
      filters.endDate &&
      filters.startDate &&
      dayjs(filters.endDate).isBefore(dayjs(filters.startDate))
    ) {
      Swal.fire(
        "Invalid Date Range",
        "End Date cannot be before Start Date.",
        "error"
      );
      return;
    }
    setCurrPage(0);
    fetchTransactions(0, pageSize, filters);
  };

  return (
    <Box style={{ padding: "2rem", marginTop: "calc(3% + 56px)" }}>
      <ToastContainer />
      <Grid>
        <Grid
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1%",
            justifyContent: "start",
          }}
        >
          <Typography
            color="textSecondary"
            style={{ fontFamily: "sans-serif", fontSize: "13px" }}
          >
            Reports
          </Typography>
          <PartnerArrowIcon />
          <Typography
            color="textSecondary"
            style={{ fontFamily: "sans-serif", fontSize: "13px" }}
          >
            Manually Approved Transactions
          </Typography>
        </Grid>
        <Grid
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "1%",
          }}
        >
          <Grid>
            <Typography
              style={{
                fontWeight: "bold",
                fontFamily: "sans-serif",
                fontSize: "20px",
                color: "#343C6A",
              }}
            >
              Manually Approved Transactions
            </Typography>
          </Grid>
          <Grid item>
            <IconButton onClick={handleFilterMenuClick} color="primary">
              <FilterAltIcon />
            </IconButton>
            <Menu
              anchorEl={filterMenuAnchor}
              open={Boolean(filterMenuAnchor)}
              onClose={handleFilterMenuClose}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box
                  sx={{
                    padding: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <MobileDatePicker
                    label="Start Date"
                    value={filters.startDate}
                    onChange={(newValue) =>
                      handleDateChange("startDate", newValue)
                    }
                    renderInput={(params) => (
                      <TextField {...params} fullWidth />
                    )}
                  />
                  <MobileDatePicker
                    label="End Date"
                    value={filters.endDate}
                    onChange={(newValue) =>
                      handleDateChange("endDate", newValue)
                    }
                    renderInput={(params) => (
                      <TextField {...params} fullWidth />
                    )}
                  />
                  <TextField
                    label="Search"
                    variant="outlined"
                    value={filters.searchBy}
                    onChange={handleSearchChange}
                    fullWidth
                  />
                  <Button onClick={applyFilters} variant="contained" fullWidth>
                    Apply Filters
                  </Button>
                </Box>
              </LocalizationProvider>
            </Menu>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12} sx={{ marginTop: "2%" }}>
        <TableContainer
          component={Paper}
          sx={{ borderRadius: "12px 12px 0px 0px" }}
        >
          <Table>
            <TableHead sx={{ backgroundColor: "#027BFE" }}>
              <TableRow>
                <TableCell sx={{ color: "white" }}>Sr. No.</TableCell>
                <TableCell sx={{ color: "white" }}>Txn ID</TableCell>
                <TableCell sx={{ color: "white" }}>Amount</TableCell>
                <TableCell sx={{ color: "white" }}>Payee Account</TableCell>
                <TableCell sx={{ color: "white" }}>Payee Name</TableCell>
                <TableCell sx={{ color: "white" }}>Date & Time</TableCell>
                <TableCell sx={{ color: "white" }}>Transfer Mode</TableCell>
                <TableCell sx={{ color: "white" }}>UTR</TableCell>
                <TableCell sx={{ color: "white" }}>Modified By</TableCell>
                <TableCell sx={{ color: "white" }}>Status</TableCell>
                <TableCell sx={{ color: "white" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            {loading ? (
              <Typography variant="h6" style={{ textAlign: "center" }}>
                Loading...
              </Typography>
            ) : (
              <TableBody>
                {txnList.map((txn, index) => (
                  <TableRow key={txn.payout?.externalRef || txn.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{txn.payout?.externalRef}</TableCell>
                    <TableCell>₹{txn.payout?.txnValue}</TableCell>
                    <TableCell>{txn.payout?.payeeAccount}</TableCell>
                    <TableCell>{txn.payout?.payeeName}</TableCell>
                    <TableCell>
                      {new Date(txn.payout.timestamp).toLocaleDateString()}{" "}
                      {new Date(txn.payout.timestamp).toLocaleTimeString()}
                    </TableCell>
                    <TableCell>{txn.payout?.transfer_mode}</TableCell>
                    <TableCell>
                      {editingRowId === (txn.payout?.externalRef || txn.id) ? (
                        <TextField
                          value={editFormData.utr}
                          onChange={(e) =>
                            handleEditFormChange("utr", e.target.value)
                          }
                          size="small"
                          variant="outlined"
                          fullWidth
                        />
                      ) : (
                        txn.payout?.txnReferenceId
                      )}
                    </TableCell>
                    <TableCell>
                      {txn?.handleBy?.handleByUser || "N/A"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={txn.payout?.status || "N/A"}
                        color={
                          txn.payout?.status === "Transaction Successful" ||
                          txn.payout?.status === "Modified"
                            ? "success"
                            : txn.payout?.status === "Reject"
                              ? "error"
                              : "default"
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {editingRowId === (txn.payout?.externalRef || txn.id) ? (
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <IconButton
                            onClick={() =>
                              handleSaveModifiedUTR(
                                txn.payout?.externalRef || txn.id
                              )
                            }
                            size="small"
                            color="primary"
                            title="Save UTR"
                          >
                            <SaveIcon />
                          </IconButton>
                          <IconButton
                            onClick={handleCancelEdit}
                            size="small"
                            color="secondary"
                            title="Cancel Edit"
                          >
                            <CancelIcon />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <IconButton
                            onClick={() => handleEditRow(txn)}
                            size="small"
                            title="Edit UTR"
                          >
                            <EditIcon />
                          </IconButton>
                          <Button
                            onClick={() =>
                              handleRejectTransaction(
                                txn.payout?.externalRef || txn.id,
                                txn.payout?.txnReferenceId || ""
                              )
                            }
                            size="small"
                            variant="outlined"
                            color="error"
                            sx={{ textTransform: "none" }}
                          >
                            Reject
                          </Button>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalElements}
          rowsPerPage={pageSize}
          page={currPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ marginTop: "1rem" }}
        />
      </Grid>

      {/* Add IPIN Modals at the end of the component */}
      {(() => {
        const isTxnMpinSet = sessionStorage.getItem("isTxnMpinSet");
        console.log(
          "[ManuallyApprovedTxnReport] Rendering modals. isIpinModalOpen:",
          isIpinModalOpen,
          "openSetPasswordModal:",
          openSetPasswordModal,
          "sessionStorage isTxnMpinSet:",
          isTxnMpinSet
        );
        if (isTxnMpinSet === "False") {
          return (
            <SetPasswordModal
              open={openSetPasswordModal}
              onClose={handleCloseIpinModals}
              onMpinValid={handleIpinVerificationSuccess}
            />
          );
        } else {
          return (
            <IpinModal
              open={isIpinModalOpen}
              onClose={handleCloseIpinModals}
              onMpinValid={handleIpinVerificationSuccess}
            />
          );
        }
      })()}
    </Box>
  );
};

export default ManuallyApprovedTxnReport;
